/**
 * Utilities for handling domain-specific logic in a way that works both
 * during pre-rendering (React Snap) and in the browser.
 */

// Get cookies to help with subdomain detection
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

// Get URL parameters to help with subdomain detection
const getUrlParam = (name: string): string | null => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

// Safely determine hostname in any environment
export const getHostname = (): string => {
  console.log('🔧 DOMAIN DETECTION: getHostname() called');
  
  // During pre-rendering with React Snap
  if (typeof window === 'undefined') {
    console.log('🔧 DOMAIN DETECTION: window is undefined (SSR), returning localhost');
    return 'localhost'; // Default hostname during pre-rendering
  }
  
  // Check for prerender signals first (these are set during prerendering)
  const prerenderSubdomain = (window as any).PRERENDER_SUBDOMAIN;
  if (prerenderSubdomain) {
    const hostname = `${prerenderSubdomain}.niopractice.com`;
    console.log('🔍 DOMAIN DETECTION: Using PRERENDER_SUBDOMAIN:', prerenderSubdomain, '→', hostname);
    return hostname;
  }
  
  // Check cookie as backup
  const cookieSubdomain = getCookie('subdomain');
  if (cookieSubdomain) {
    const hostname = `${cookieSubdomain}.niopractice.com`;
    console.log('🔍 DOMAIN DETECTION: Using cookie subdomain:', cookieSubdomain, '→', hostname);
    return hostname;
  }
  
  // Check URL parameter as another backup
  const urlSubdomain = getUrlParam('prerender');
  if (urlSubdomain) {
    const hostname = `${urlSubdomain}.niopractice.com`;
    console.log('🔍 DOMAIN DETECTION: Using URL parameter subdomain:', urlSubdomain, '→', hostname);
    return hostname;
  }
  
  // In the browser - use the actual hostname (which may be overridden during pre-rendering)
  const actualHostname = window.location.hostname;
  console.log('🔍 DOMAIN DETECTION: Using actual hostname:', actualHostname);
  return actualHostname;
};

// Check if current hostname is SSC-specific
export const isSSCDomain = (): boolean => {
  console.log('🔧 SSC DOMAIN DETECTION: isSSCDomain() called');
  const hostname = getHostname();
  const isSSC = hostname.includes('ssc');
  console.log('🔍 SSC DOMAIN DETECTION: hostname:', hostname, '| contains "ssc":', isSSC);
  return isSSC;
};

// Check if current hostname is GATE-specific
export const isGATEDomain = (): boolean => {
  console.log('🔧 GATE DOMAIN DETECTION: isGATEDomain() called');
  const hostname = getHostname();
  const isGATE = hostname.includes('gate');
  console.log('🔍 GATE DOMAIN DETECTION: hostname:', hostname, '| contains "gate":', isGATE);
  return isGATE;
};

// Get domain type for routing decisions
export const getDomainType = (): 'ssc' | 'gate' | 'ibps' | 'rrb' | 'iitjee' | 'global' | 'local' | 'simplified' | 'default' => {
  console.log('🔧 DOMAIN TYPE DETECTION: getDomainType() called');
  const hostname = getHostname().toLowerCase();
  
  // Check for specific domain types in order of priority
  if (hostname.includes('ssc')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected SSC domain');
    return 'ssc';
  }
  if (hostname.includes('gate')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected GATE domain');
    return 'gate';
  }
  if (hostname.includes('ibps')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected IBPS domain');
    return 'ibps';
  }
  if (hostname.includes('rrb')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected RRB domain');
    return 'rrb';
  }
  if (hostname.includes('iitjee')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected IITJEE domain');
    return 'iitjee';
  }
  if (hostname.includes('global')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected Global domain');
    return 'global';
  }
  if (hostname.includes('local')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected Local domain');
    return 'local';
  }
  if (hostname.includes('simplified')) {
    console.log('🔍 DOMAIN TYPE DETECTION: Detected Simplified domain');
    return 'simplified';
  }
  
  console.log('🔍 DOMAIN TYPE DETECTION: No specific domain detected, returning default');
  return 'default';
};

// Safe domain detection that works during prerendering and live
export const getDomainTypeForLanding = (): string => {
  console.log('🔧 LANDING DOMAIN DETECTION: getDomainTypeForLanding() called');
  
  try {
    // During prerendering, use our enhanced domain detection
    if (typeof window !== 'undefined' && 
        ((window as any).PRERENDER_SUBDOMAIN || 
         document.cookie.includes('subdomain=') ||
         new URLSearchParams(window.location.search).get('prerender'))) {
      const domainType = getDomainType();
      console.log('🔍 LANDING DOMAIN DETECTION: Using enhanced detection for prerendering:', domainType);
      return domainType;
    }
    
    // For live domains, fall back to the original window.location.host logic
    if (typeof window !== 'undefined') {
      const host = window.location.host?.toLowerCase();
      console.log('🔍 LANDING DOMAIN DETECTION: Using live host detection:', host);
      
      if (host?.includes('ssc')) return 'ssc';
      if (host?.includes('gate')) return 'gate';
      if (host?.includes('ibps')) return 'ibps';
      if (host?.includes('rrb')) return 'rrb';
      if (host?.includes('iitjee')) {
        console.log('🔍 LANDING DOMAIN DETECTION: Detected IITJEE domain');
        return 'iitjee';
      }
      if (host?.includes('global')) return 'global';
      if (host?.includes('local')) return 'local';
      if (host?.includes('simplified')) return 'simplified';
    }
    
    console.log('🔍 LANDING DOMAIN DETECTION: No specific domain detected, returning default');
    return 'default';
  } catch (error) {
    console.error('❌ Error in getDomainTypeForLanding:', error);
    return 'default';
  }
};

// Get domain-specific content/settings
export const getDomainSettings = () => {
  const domainType = getDomainType();
  
  switch (domainType) {
    case 'ssc':
      return {
        title: 'SSC Preparation | NioPractice',
        description: 'Adaptive and interactive tools to crack SSC. Practice with AI-powered tests, track your progress, and master every topic for SSC exams.',
        keywords: 'SSC, Government Exams, SSC CGL, SSC CHSL, Exam Preparation'
      };
    case 'gate':
      return {
        title: 'GATE Preparation | NioPractice',
        description: 'Prepare for GATE with AI-powered practice tests. Comprehensive coverage of CS subjects, previous year questions, and detailed solutions.',
        keywords: 'GATE Exam 2025, GATE preparation, GATE mock tests, chapter-wise test for GATE exam, topic-wise test for GATE exam, GATE practice tests, GATE study material, GATE syllabus, Engineering exam preparation, GATE exam pattern, GATE tips, GATE books, GATE revision'
      };
    default:
      return {
        title: 'NioPractice - AI-Powered Learning',
        description: 'Adaptive and interactive self-study tools to ace any test. AI-powered learning platform for personalized practice and improvement.',
        keywords: 'AI driven exams, ai exams, custom chapter test'
      };
  }
}; 